import React, { useEffect, useMemo, useState } from "react";
import { getTransform } from "../../../../../utils/getTransform";
import { renderBlurLivePreviewUrl } from "../../../lib/applyBlurEffect";
import {
  BLUR_AMOUNT_MAX,
  BLUR_TYPES,
  clampBlurAmount,
  clampPixelParam,
  getBlurPreviewStyle,
  resolveBlurType,
  resolvePixelShape,
} from "../../../lib/blurPrepModel";
import {
  aspectFromSize,
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../../shared/Popup/ToolPrepStage.css";
import "./BlurPreview.css";

const MOTION_TRAIL_STEPS = 11;

const CANVAS_PREVIEW_TYPES = new Set([
  BLUR_TYPES.PIXELATE,
  BLUR_TYPES.SQUARE_BOKEH,
]);

function BlurredLayer({
  src,
  className,
  fittedStyle,
  isPixelate,
  pixelScale,
  blurAmount,
  previewStyle,
}) {
  if (previewStyle.motionTrail && blurAmount > 0 && fittedStyle) {
    const dist = previewStyle.motionDistance || 12;
    const layers = [];
    for (let i = 0; i < MOTION_TRAIL_STEPS; i++) {
      const t = MOTION_TRAIL_STEPS === 1 ? 0 : i / (MOTION_TRAIL_STEPS - 1) - 0.5;
      const ox = dist * t * 2;
      const weight = 1 - Math.abs(t) * 0.65;
      layers.push(
        <img
          key={i}
          src={src}
          alt=""
          className={`${className} blur-preview__layer--motion`}
          style={{
            ...fittedStyle,
            left: fittedStyle.left + ox,
            opacity: weight / (MOTION_TRAIL_STEPS * 0.55),
            filter: "none",
          }}
          draggable={false}
        />
      );
    }
    return <div className="blur-preview__motion-stack">{layers}</div>;
  }

  if (isPixelate && blurAmount > 0 && fittedStyle) {
    return (
      <img
        src={src}
        alt=""
        className={`${className} blur-preview__layer--pixelate`}
        style={{
          width: fittedStyle.width * pixelScale,
          height: fittedStyle.height * pixelScale,
          left: fittedStyle.left + (fittedStyle.width * (1 - pixelScale)) / 2,
          top: fittedStyle.top + (fittedStyle.height * (1 - pixelScale)) / 2,
          transform: `scale(${1 / pixelScale})`,
          transformOrigin: "center center",
          imageRendering: "pixelated",
        }}
        draggable={false}
      />
    );
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      style={{
        ...fittedStyle,
        filter: blurAmount > 0 ? previewStyle.filter || "none" : "none",
      }}
      draggable={false}
    />
  );
}

/**
 * Photoroom-style blur preview:
 * - No cutout → blur the entire image
 * - With cutout → sharp scene (original punched) + blur on the object only
 * - Pixelate / Square Pixel → canvas mosaic bake (matches Apply)
 */
const BlurPreview = ({
  imageUrl = null,
  cutoutUrl = null,
  type = BLUR_TYPES.GAUSSIAN,
  amount = 20,
  pixelShape = "hex",
  pixelGap = 0,
  pixelSoftness = 0,
  pixelMix = 100,
  blurTarget = "full",
  gaussianMix = 100,
  edgePreserve = 0,
  forceCanvasPreview = false,
  loading = false,
  applying = false,
  applyingLabel = "Applying blur…",
  emptyLabel = "Upload an image to preview blur",
  showSubjectBox = false,
}) => {
  const naturalSize = useImageNaturalSize(imageUrl || cutoutUrl);
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const [mosaicPreviewUrl, setMosaicPreviewUrl] = useState(null);
  const [mosaicPreviewBusy, setMosaicPreviewBusy] = useState(false);
  const stageRef = React.useRef(null);
  const mosaicUrlRef = React.useRef(null);
  const blurAmount = clampBlurAmount(amount);
  const blurType = resolveBlurType(type);
  const resolvedPixelShape = resolvePixelShape(pixelShape);
  const resolvedPixelGap = clampPixelParam(pixelGap, 0);
  const resolvedPixelSoftness = clampPixelParam(pixelSoftness, 0);
  const resolvedPixelMix = clampPixelParam(pixelMix, 100);
  const resolvedGaussianMix = clampPixelParam(gaussianMix, 100);
  const resolvedEdgePreserve = clampPixelParam(edgePreserve, 0);
  const resolvedBlurTarget = blurTarget || (cutoutUrl ? "subject" : "full");
  const isEmpty = !imageUrl && !loading;
  const objectMode = Boolean(cutoutUrl) && !loading;
  const usesCanvasMosaic =
    (forceCanvasPreview || CANVAS_PREVIEW_TYPES.has(blurType)) &&
    blurAmount > 0 &&
    Boolean(imageUrl);

  const imageAspect = useMemo(
    () => aspectFromSize(naturalSize.width, naturalSize.height, 1),
    [naturalSize]
  );

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const measure = () => {
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        imageAspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (!fitted) return;
      setStageSize(fitted);
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [imageAspect, imageUrl, cutoutUrl]);

  useEffect(() => {
    if (!usesCanvasMosaic) {
      if (mosaicUrlRef.current) {
        URL.revokeObjectURL(mosaicUrlRef.current);
        mosaicUrlRef.current = null;
      }
      setMosaicPreviewUrl(null);
      setMosaicPreviewBusy(false);
      return undefined;
    }

    let cancelled = false;
    setMosaicPreviewBusy(true);
    const timer = window.setTimeout(() => {
      renderBlurLivePreviewUrl({
        sourceImageUrl: imageUrl,
        cutoutUrl: cutoutUrl || null,
        type: blurType,
        amount: blurAmount,
        pixelShape: resolvedPixelShape,
        pixelGap: resolvedPixelGap,
        pixelSoftness: resolvedPixelSoftness,
        pixelMix: resolvedPixelMix,
        blurTarget: resolvedBlurTarget,
        gaussianMix: resolvedGaussianMix,
        edgePreserve: resolvedEdgePreserve,
        maxEdge: 720,
      })
        .then((url) => {
          if (cancelled) {
            if (url) URL.revokeObjectURL(url);
            return;
          }
          if (mosaicUrlRef.current) URL.revokeObjectURL(mosaicUrlRef.current);
          mosaicUrlRef.current = url;
          setMosaicPreviewUrl(url);
        })
        .catch(() => {
          if (!cancelled) setMosaicPreviewUrl(null);
        })
        .finally(() => {
          if (!cancelled) setMosaicPreviewBusy(false);
        });
    }, 60);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    usesCanvasMosaic,
    imageUrl,
    cutoutUrl,
    blurType,
    blurAmount,
    resolvedPixelShape,
    resolvedPixelGap,
    resolvedPixelSoftness,
    resolvedPixelMix,
    resolvedBlurTarget,
    resolvedGaussianMix,
    resolvedEdgePreserve,
  ]);

  useEffect(
    () => () => {
      if (mosaicUrlRef.current) {
        URL.revokeObjectURL(mosaicUrlRef.current);
        mosaicUrlRef.current = null;
      }
    },
    []
  );

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height || !stageSize.width) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      "fit"
    );
  }, [naturalSize, stageSize]);

  const displayBlurPx = useMemo(() => {
    if (!transform.width || blurAmount <= 0) return 0;
    const t = blurAmount / BLUR_AMOUNT_MAX;
    return Math.max(2, t * transform.width * 0.16);
  }, [blurAmount, transform.width]);

  const amount01 = blurAmount / BLUR_AMOUNT_MAX;

  const previewStyle = useMemo(
    () => getBlurPreviewStyle(blurType, displayBlurPx, amount01),
    [amount01, blurType, displayBlurPx]
  );

  const fittedStyle =
    transform.width > 0
      ? {
          width: transform.width,
          height: transform.height,
          left: transform.x,
          top: transform.y,
        }
      : undefined;

  const fitFrame = useMemo(() => {
    if (!fittedStyle) return null;
    return {
      x: fittedStyle.left,
      y: fittedStyle.top,
      width: fittedStyle.width,
      height: fittedStyle.height,
    };
  }, [fittedStyle?.left, fittedStyle?.top, fittedStyle?.width, fittedStyle?.height]);

  const interactive =
    showSubjectBox && Boolean(cutoutUrl) && !loading && !applying;
  const [isCropping, setIsCropping] = useState(false);
  const {
    object,
    setObject,
    wrapperStyle,
    imageStyle: cropImgStyle,
    crop,
    cropExtent,
    originalImageSize,
  } = useToolPrepSubjectObject({
      enabled: interactive,
      imageUrl: cutoutUrl,
      fitFrame,
      resetKey: cutoutUrl,
    });

  const liveFittedStyle = interactive && object
    ? {
        left: object.x,
        top: object.y,
        width: object.width,
        height: object.height,
      }
    : fittedStyle;

  const isPixelate = previewStyle.pixelateScale != null;
  const pixelScale = previewStyle.pixelateScale || 1;

  const punchedOriginalStyle = useMemo(() => {
    if (!fittedStyle || !cutoutUrl) return fittedStyle;
    return {
      ...fittedStyle,
      WebkitMaskImage: `url(${cutoutUrl}), linear-gradient(#fff, #fff)`,
      WebkitMaskSize: "100% 100%, 100% 100%",
      WebkitMaskComposite: "destination-out",
      maskImage: `url(${cutoutUrl}), linear-gradient(#fff, #fff)`,
      maskSize: "100% 100%, 100% 100%",
      maskComposite: "exclude",
    };
  }, [cutoutUrl, fittedStyle]);

  return (
    <ToolPrepCanvasStage
      className="blur-tool-preview"
      ref={stageRef}
      metaImageUrl={imageUrl || cutoutUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      <div
        className={`blur-preview__stage${
          isEmpty || objectMode ? " blur-preview__stage--checker" : ""
        }`}
        style={{
          width: stageSize.width,
          height: stageSize.height,
        }}
      >
        <div className="tool-prep-stage__clip">
          {imageUrl && liveFittedStyle ? (
            usesCanvasMosaic ? (
              <img
                src={mosaicPreviewUrl || imageUrl}
                alt=""
                className="blur-preview__layer blur-preview__layer--mosaic"
                style={liveFittedStyle}
                draggable={false}
              />
            ) : objectMode && interactive && object && wrapperStyle ? (
              <>
                <img
                  src={imageUrl}
                  alt=""
                  className="blur-preview__layer blur-preview__layer--scene"
                  style={punchedOriginalStyle}
                  draggable={false}
                />
                <div
                  className="blur-preview__subject"
                  style={subjectWrapperStyleForCrop(wrapperStyle, isCropping)}
                >
                  <ToolPrepSubjectCropGhost
                    active={isCropping}
                    src={cutoutUrl}
                    style={cropImgStyle}
                  />
                  <img
                    src={cutoutUrl}
                    alt=""
                    className="blur-preview__layer blur-preview__layer--object"
                    style={subjectMainImageStyleForCrop(
                      {
                        ...cropImgStyle,
                        filter:
                          !isPixelate && previewStyle.filter
                            ? previewStyle.filter
                            : blurAmount > 0 && !isPixelate
                              ? `blur(${displayBlurPx}px)`
                              : undefined,
                      },
                      { isCropping, crop, originalImageSize, object }
                    )}
                    draggable={false}
                  />
                </div>
              </>
            ) : objectMode ? (
              <>
                <img
                  src={imageUrl}
                  alt=""
                  className="blur-preview__layer blur-preview__layer--scene"
                  style={punchedOriginalStyle}
                  draggable={false}
                />
                <BlurredLayer
                  src={cutoutUrl}
                  className="blur-preview__layer blur-preview__layer--object"
                  fittedStyle={liveFittedStyle}
                  isPixelate={isPixelate}
                  pixelScale={pixelScale}
                  blurAmount={blurAmount}
                  previewStyle={previewStyle}
                />
              </>
            ) : (
              <BlurredLayer
                src={imageUrl}
                className="blur-preview__layer blur-preview__layer--full"
                fittedStyle={liveFittedStyle}
                isPixelate={isPixelate}
                pixelScale={pixelScale}
                blurAmount={blurAmount}
                previewStyle={previewStyle}
              />
            )
          ) : null}

          {(loading ||
            applying ||
            (usesCanvasMosaic && mosaicPreviewBusy && !mosaicPreviewUrl)) && (
            <div className="blur-preview__overlay">
              <RoundLoader size={36} />
              <span className="blur-preview__overlay-label">
                {applying
                  ? applyingLabel
                  : loading
                    ? "Removing background…"
                    : null}
              </span>
            </div>
          )}

          {isEmpty && !loading ? (
            <div className="blur-preview__empty">{emptyLabel}</div>
          ) : null}
        </div>

        {showSubjectBox && cutoutUrl && object && !loading && !applying ? (
          <div className="tool-prep-stage__chrome">
            <ToolPrepSubjectBoundingBox
              enabled
              interactive
              frame={object}
              crop={crop}
              cropExtent={cropExtent}
              originalImageSize={originalImageSize}
              onChange={setObject}
              onCroppingChange={setIsCropping}
              stageSize={stageSize}
            />
          </div>
        ) : null}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default BlurPreview;
